import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import NavigationBreadcrumb from '../../components/ui/NavigationBreadcrumb';
import FooterSection from '../homepage/components/FooterSection';

const CommonMistakesGuide = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedMistake, setExpandedMistake] = useState(null);

  const categories = [
    { id: 'all', label: 'Alle fouten', icon: 'AlertTriangle' },
    { id: 'solar', label: 'Zonnepanelen', icon: 'Sun' },
    { id: 'insulation', label: 'Isolatie', icon: 'Home' },
    { id: 'heatpump', label: 'Warmtepomp', icon: 'Thermometer' },
    { id: 'ventilation', label: 'Ventilatie', icon: 'Wind' },
  ];

  const commonMistakes = [
    {
      id: 1,
      category: 'solar',
      title: 'Te weinig panelen geïnstalleerd',
      impact: 'Hoog',
      cost: '€2.000 - €5.000',
      description: 'Veel huiseigenaren onderschatten hun energieverbruik en installeren te weinig panelen.',
      consequences: [
        'Lagere energieopbrengst dan verwacht',
        'Langere terugverdientijd',
        'Blijvende afhankelijkheid van het energienet'
      ],
      prevention: [
        'Laat een professionele energiescan uitvoeren',
        'Bereken toekomstig verbruik (elektrische auto, warmtepomp)',
        'Plan ruimte voor uitbreiding'
      ],
      realExample: 'Familie Jansen installeerde 8 panelen maar had er 14 nodig. Na 2 jaar moesten ze uitbreiden voor €3.500 extra kosten.'
    },
    {
      id: 2,
      category: 'insulation',
      title: 'Verkeerde isolatievolgorde',
      impact: 'Hoog',
      cost: '€1.500 - €3.000',
      description: 'Beginnen met vloerisolatie terwijl het dak nog niet geïsoleerd is.',
      consequences: [
        'Warmte ontsnapt via het dak',
        'Inefficiënte investering',
        'Vocht- en schimmelproblemen'
      ],
      prevention: [
        'Volg de juiste volgorde: dak → muren → vloer → glas',
        'Laat een isolatieadvies opstellen',
        'Plan alle isolatie in één keer'
      ],
      realExample: 'Meneer De Vries isoleerde eerst zijn vloer voor €4.000, maar merkte nauwelijks verschil omdat 40% warmte via het dak ontsnapte.'
    },
    {
      id: 3,
      category: 'heatpump',
      title: 'Warmtepomp zonder isolatie',
      impact: 'Zeer hoog',
      cost: '€5.000 - €10.000',
      description: 'Een warmtepomp installeren in een slecht geïsoleerd huis.',
      consequences: [
        'Extreem hoge energiekosten',
        'Warmtepomp draait constant',
        'Huis wordt niet warm genoeg'
      ],
      prevention: [
        'Eerst isoleren, dan warmtepomp',
        'Minimaal label B vereist',
        'Laat een warmteverliesberekening maken'
      ],
      realExample: 'Familie Bakker investeerde €15.000 in een warmtepomp maar had €400/maand aan stroom. Na isolatie daalde dit naar €120/maand.'
    },
    {
      id: 4,
      category: 'solar',
      title: 'Geen rekening met schaduw',
      impact: 'Gemiddeld',
      cost: '€500 - €2.000',
      description: 'Panelen plaatsen zonder schaduwanalyse van bomen, schoorstenen of buren.',
      consequences: [
        '20-40% minder opbrengst',
        'Ongelijke belasting van panelen',
        'Kortere levensduur'
      ],
      prevention: [
        'Vraag om een schaduwanalyse',
        'Gebruik micro-omvormers bij gedeeltelijke schaduw',
        'Overweeg alternatieve plaatsing'
      ],
      realExample: 'De schoorsteen van de buren zorgde voor 30% opbrengstverlies bij de installatie van meneer Visser.'
    },
    {
      id: 5,
      category: 'ventilation',
      title: 'Ventilatie vergeten bij isolatie',
      impact: 'Hoog',
      cost: '€3.000 - €6.000',
      description: 'Na isolatie geen mechanische ventilatie installeren.',
      consequences: [
        'Vochtproblemen en schimmel',
        'Ongezonde binnenlucht',
        'Schade aan isolatie en constructie'
      ],
      prevention: [
        'Plan ventilatie tegelijk met isolatie',
        'Kies voor WTW-ventilatie (warmteterugwinning)',
        'Laat luchtdichtheid testen'
      ],
      realExample: 'Na isolatie kreeg familie Smit binnen 6 maanden schimmel. Ventilatie-installatie kostte alsnog €5.000.'
    },
    {
      id: 6,
      category: 'insulation',
      title: 'Goedkoopste offerte kiezen',
      impact: 'Gemiddeld',
      cost: '€2.000 - €4.000',
      description: 'Kiezen voor de laagste prijs zonder kwaliteitscontrole.',
      consequences: [
        'Slechte uitvoering',
        'Koudebruggen en lekken',
        'Geen garantie of service'
      ],
      prevention: [
        'Vergelijk minimaal 3 offertes',
        'Check certificeringen en reviews',
        'Vraag naar garanties en nazorg'
      ],
      realExample: 'Mevrouw Peters koos de goedkoopste isolatie. Na 1 jaar bleken er koudebruggen en moest alles opnieuw voor €6.000.'
    },
  ];

  const filteredMistakes = selectedCategory === 'all' 
    ? commonMistakes 
    : commonMistakes?.filter(m => m?.category === selectedCategory);

  const impactColors = {
    'Zeer hoog': 'text-red-600 bg-red-50 border-red-200',
    'Hoog': 'text-orange-600 bg-orange-50 border-orange-200',
    'Gemiddeld': 'text-yellow-600 bg-yellow-50 border-yellow-200',
  };

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation />
      <NavigationBreadcrumb />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 py-16 relative overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-10"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{ duration: 20, repeat: Infinity, repeatType: 'reverse' }}
          style={{
            backgroundImage: 'radial-gradient(circle, #ef4444 1px, transparent 1px)',
            backgroundSize: '50px 50px',
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full mb-6"
              animate={{ scale: [1, 1.05, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <Icon name="AlertTriangle" size={20} />
              <span className="font-semibold">Veelgemaakte Fouten</span>
            </motion.div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Leer van de <span className="text-red-600">fouten</span> van anderen
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Vermijd kostbare vergissingen bij uw verduurzaming. Ontdek de meest voorkomende fouten en hoe u ze voorkomt.
            </p>
            <div className="flex items-center justify-center space-x-6 text-sm">
              <div className="flex items-center space-x-2">
                <Icon name="Users" size={18} className="text-red-600" />
                <span>Gebaseerd op 500+ ervaringen</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="TrendingDown" size={18} className="text-red-600" />
                <span>Bespaar €2.000 - €10.000</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* Category Filter */}
      <section className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-3 overflow-x-auto">
            {categories?.map((cat) => (
              <motion.button
                key={cat?.id}
                onClick={() => setSelectedCategory(cat?.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                  selectedCategory === cat?.id
                    ? 'bg-red-600 text-white shadow-lg'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Icon name={cat?.icon} size={18} />
                <span className="font-medium">{cat?.label}</span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>
      {/* Mistakes Grid */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={selectedCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 lg:grid-cols-2 gap-6"
            >
              {filteredMistakes?.map((mistake, index) => (
                <motion.div
                  key={mistake?.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg overflow-hidden border-2 border-gray-100 hover:shadow-2xl transition-all"
                  style={{
                    transform: 'perspective(1000px) rotateX(0deg)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.1), 0 1px 8px rgba(0,0,0,0.06)'
                  }}
                  whileHover={{
                    y: -8,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15), 0 5px 15px rgba(0,0,0,0.1)'
                  }}
                >
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {mistake?.title}
                        </h3>
                        <p className="text-gray-600 text-sm">{mistake?.description}</p>
                      </div>
                      <motion.div
                        animate={{ rotate: [0, -10, 10, -10, 0] }}
                        transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                      >
                        <Icon name="AlertCircle" size={32} className="text-red-500" />
                      </motion.div>
                    </div>

                    {/* Impact & Cost */}
                    <div className="flex items-center space-x-3 mb-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${impactColors?.[mistake?.impact]}`}>
                        Impact: {mistake?.impact}
                      </span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700">
                        💰 {mistake?.cost}
                      </span>
                    </div>

                    {/* Expandable Content */}
                    <motion.button
                      onClick={() => setExpandedMistake(expandedMistake === mistake?.id ? null : mistake?.id)}
                      className="w-full text-left"
                    >
                      <div className="flex items-center justify-between py-2 border-t border-gray-200">
                        <span className="text-sm font-semibold text-red-600">Bekijk details & preventie</span>
                        <motion.div
                          animate={{ rotate: expandedMistake === mistake?.id ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <Icon name="ChevronDown" size={20} className="text-red-600" />
                        </motion.div>
                      </div>
                    </motion.button>

                    <AnimatePresence>
                      {expandedMistake === mistake?.id && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pt-4 space-y-4">
                            {/* Consequences */}
                            <div className="bg-red-50 rounded-lg p-4">
                              <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                                <Icon name="XCircle" size={18} className="mr-2" />
                                Gevolgen
                              </h4>
                              <ul className="space-y-1">
                                {mistake?.consequences?.map((cons, i) => (
                                  <li key={i} className="text-sm text-red-800 flex items-start">
                                    <span className="mr-2">•</span>
                                    <span>{cons}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Prevention */}
                            <div className="bg-green-50 rounded-lg p-4">
                              <h4 className="font-semibold text-green-900 mb-2 flex items-center">
                                <Icon name="CheckCircle" size={18} className="mr-2" />
                                Hoe te voorkomen
                              </h4>
                              <ul className="space-y-1">
                                {mistake?.prevention?.map((prev, i) => (
                                  <li key={i} className="text-sm text-green-800 flex items-start">
                                    <span className="mr-2">✓</span>
                                    <span>{prev}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Real Example */}
                            <div className="bg-blue-50 rounded-lg p-4">
                              <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                                <Icon name="MessageSquare" size={18} className="mr-2" />
                                Echt voorbeeld
                              </h4>
                              <p className="text-sm text-blue-800 italic">"{mistake?.realExample}"</p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">Voorkom deze fouten bij uw project</h2>
            <p className="text-xl mb-8 opacity-90">
              Laat u adviseren door gecertificeerde experts en krijg een foutloos advies op maat.
            </p>
            <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100">
              Vraag gratis advies aan
            </Button>
          </motion.div>
        </div>
      </section>
      <FooterSection />
    </div>
  );
};

export default CommonMistakesGuide;